import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { RootState } from '../store/store';
import useUnreadCount from './useUnreadCount';
import useAdminQueueCounts from './useAdminQueueCounts';

/** The two admin queues a visit to should stop counting toward the badge. */
const ESCALATIONS_PATH = '/admin/escalations';
/** Legacy URL — kept only so a redirect through it still reads as "open". */
const AGENT_APPROVALS_PATH = '/admin/agent-approvals';
/** The unified task inbox, where approvals are actually worked now. */
const TASK_INBOX_PATH = '/admin';

type BadgeNavigator = Navigator & {
  setAppBadge?: (contents?: number) => Promise<void>;
  clearAppBadge?: () => Promise<void>;
};

export const isAppBadgeSupported = (nav: Navigator = navigator): boolean =>
  typeof (nav as BadgeNavigator).setAppBadge === 'function' &&
  typeof (nav as BadgeNavigator).clearAppBadge === 'function';

/**
 * Sets the OS app badge, swallowing the rejection Safari throws when the page
 * is not installed — `setAppBadge` exists on iOS 16.4+ Safari even when it is
 * not running as an installed PWA, but calling it there rejects.
 */
export const applyAppBadge = (
  count: number,
  nav: Navigator = navigator
): void => {
  if (!isAppBadgeSupported(nav)) return;
  const badgeNav = nav as BadgeNavigator;
  const promise =
    count > 0 ? badgeNav.setAppBadge?.(count) : badgeNav.clearAppBadge?.();
  promise?.catch(() => {
    // Not installed, or the platform declined — nothing to surface to the user.
  });
};

/**
 * Drives `navigator.setAppBadge` / `clearAppBadge` (#310) from the same data
 * already powering the notification bell and, for admins, the two operator
 * queues. No new polling: both counts are fetched once and then kept live
 * over sockets the rest of the app already subscribes to.
 *
 * - ADMIN: open escalations (OPEN/IN_PROGRESS) + pending approvals. Visiting
 *   the corresponding queue — `/admin/escalations`, or the task inbox at
 *   `/admin` for approvals — stops that half of the count from contributing
 *   to the badge, even before anything in it is resolved.
 * - Everyone else: unread in-app notifications, exactly as the bell shows it.
 *
 * A no-op everywhere the API is unsupported (most non-iOS-installed
 * browsers) — every call is guarded by {@link isAppBadgeSupported}.
 */
const useAppBadge = (): void => {
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const { pathname } = useLocation();
  const isAdmin = currentUser?.type === 'ADMIN';

  const unreadCount = useUnreadCount();
  const { openEscalations, pendingApprovals } = useAdminQueueCounts(isAdmin);

  const escalationsOpen = pathname.startsWith(ESCALATIONS_PATH);
  const approvalsOpen =
    pathname.startsWith(AGENT_APPROVALS_PATH) ||
    pathname === TASK_INBOX_PATH ||
    pathname === `${TASK_INBOX_PATH}/`;

  const adminCount =
    (escalationsOpen ? 0 : openEscalations) +
    (approvalsOpen ? 0 : pendingApprovals);
  const count = !currentUser ? 0 : isAdmin ? adminCount : unreadCount;

  const countRef = useRef(count);
  countRef.current = count;

  useEffect(() => {
    applyAppBadge(count);
  }, [count]);

  // iOS can drop the badge while the app is backgrounded; re-assert it once
  // the tab is visible again rather than waiting on the next data change.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        applyAppBadge(countRef.current);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);
};

export default useAppBadge;
